import React, { useState, useEffect } from "react";
import { X, Save } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Product, VariantForm } from "../types/product";
import type { ProductUpdates } from "../hooks/useProducts";
import { allVariants, validateVariants } from "../lib/variants";
import VariantsEditor from "./VariantsEditor";
import ImageUploader from "./utils/ImageUploader";
import RichTextEditor from "./utils/RichTextEditor";
import { cn } from "../lib/utils";

interface ProductEditOverlayProps {
  product: Product;
  onSave: (productId: string, updates: ProductUpdates, variants: VariantForm[]) => Promise<boolean>;
  onClose: () => void;
  isOpen: boolean;
}

const ProductEditOverlay: React.FC<ProductEditOverlayProps> = ({
  product,
  onSave,
  onClose,
  isOpen,
}) => {
  const [formData, setFormData] = useState<Product>(product);
  const [variants, setVariants] = useState<VariantForm[]>(() => allVariants(product));
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setFormData(product);
      setVariants(allVariants(product));
      setErrors({});
    }
  }, [isOpen, product]);

  const handleLongDescriptionChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      long_description: value,
    }));
  };

  const handleImageUpload = (imageUrl: string) => {
    setFormData((prev) => ({
      ...prev,
      image_url: imageUrl,
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Il nome del prodotto è obbligatorio";
    }

    const variantsError = validateVariants(variants);
    if (variantsError) {
      newErrors.variants = variantsError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      // Le quantità dei formati già salvati non passano da qui: vedi
      // saveVariants in useProducts.
      const success = await onSave(
        product.id,
        {
          name: formData.name,
          description: formData.description,
          long_description: formData.long_description,
          image_url: formData.image_url,
          category: formData.category,
        },
        variants
      );
      if (success) {
        onClose();
      }
    } catch (error) {
      console.error("Errore durante il salvataggio:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const labelCls = "mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-muted-foreground";
  const errorCls = "mt-1 text-xs font-semibold text-danger";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center overflow-y-auto bg-[#2F1500]/40 p-4 backdrop-blur-[2px]"
      onClick={handleOverlayClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-product-title"
        className="flex max-h-[90dvh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-card shadow-[0_24px_60px_-12px_rgba(47,21,0,.35)] ring-1 ring-border/40"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-5">
          <div className="min-w-0">
            <div className="mb-2.5 h-1 w-10 rounded-full bg-primary" />
            <h2 id="edit-product-title" className="font-display text-2xl font-semibold text-foreground">
              Modifica prodotto
            </h2>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">{product.name}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Chiudi"
            className="flex h-9 w-9 flex-none items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-5">
              <div>
                <label htmlFor="edit-name" className={labelCls}>Nome prodotto *</label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className={cn(errors.name && "border-destructive")}
                  placeholder="Es. Miele Millefiori"
                />
                {errors.name && <p className={errorCls}>{errors.name}</p>}
              </div>

              <div>
                <label htmlFor="edit-description" className={labelCls}>Descrizione breve</label>
                <Textarea
                  id="edit-description"
                  value={formData.description || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Una o due frasi mostrate nella scheda del prodotto"
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  {formData.description?.length || 0}/255 caratteri
                </p>
              </div>

            </div>

            <div className="space-y-5">
              <div>
                <label htmlFor="edit-category" className={labelCls}>Categoria</label>
                <Input
                  id="edit-category"
                  value={formData.category || ""}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="Miele, Propoli, Accessori…"
                />
              </div>
            </div>
          </div>

          <div>
            <span className={labelCls}>Formati e prezzi *</span>
            <VariantsEditor variants={variants} onChange={setVariants} error={errors.variants} />
          </div>

          <ImageUploader
            onImageUpload={handleImageUpload}
            currentImage={formData.image_url}
            productId={formData.id}
          />

          <div>
            <span className={labelCls}>Descrizione estesa</span>
            <RichTextEditor
              value={formData.long_description || ""}
              onChange={handleLongDescriptionChange}
              placeholder="La descrizione completa mostrata nella pagina del prodotto…"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-border bg-muted/50 px-6 py-4">
          <button
            onClick={onClose}
            className="h-10 rounded-lg bg-card px-4 text-[13px] font-bold text-foreground shadow-[inset_0_0_0_1px_hsl(var(--border))] hover:bg-muted"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-[13px] font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            {isSaving ? "Salvataggio…" : "Salva modifiche"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductEditOverlay;